import React from 'react';

const TermsAndPolicy = () => {
  return (
    <div className="min-h-screen bg-[#1a1a1a] py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[rgba(26,26,26,0.2)] border border-black/10 rounded-lg">
          <div className="p-8">
            <div className="space-y-8 text-white/90">
              {/* Header */}
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-white mb-2">MindRush Mobile Application</h1>
                <h2 className="text-2xl font-semibold text-white mb-4">Terms of Use, User Policy, and Privacy Policy</h2>
                <div className="text-white/60 text-sm">
                  <p>Effective Date: 15/01/2026</p>
                  <p>Last Updated: 15/01/2026</p>
                </div>
              </div>

              {/* 1. Terms of Use */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-6">1. Terms of Use</h2>
                
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">1.1 Introduction</h3>
                    <p className="text-white/80">
                      Welcome to MindRush, a mobile application owned and operated by DV Tech Ventures Co. ("Company," "we," "us," or "our"). 
                      These Terms of Use ("Terms") govern your access to and use of the MindRush app, including all features, functionalities, 
                      and services provided through the application. By downloading, accessing, or using MindRush, you agree to these Terms. 
                      If you do not agree, please refrain from using the application.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">1.2 Eligibility</h3>
                    <p className="text-white/80">To use MindRush, you must meet the following conditions:</p>
                    <ul className="list-disc pl-6 text-white/80 space-y-2 mt-2">
                      <li>Be <strong>5 years of age or older</strong>. If you are under the age of 13 (or the age of digital consent in your region), a parent/guardian may need to help you create or manage your account.</li>
                      <li>Provide accurate information (such as age/grade) so we can show appropriate question difficulty and educational content.</li>
                      <li>Have permission to use the app and accept these Terms (either on your own behalf if you are old enough, or with a parent/guardian’s consent where required by law).</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">1.3 Changes to the Terms</h3>
                    <p className="text-white/80">
                      The Company reserves the right to modify these Terms at any time without prior notice. Any updates will be posted 
                      within the application, and continued use constitutes acceptance of the revised Terms.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">1.4 User Responsibilities</h3>
                    <p className="text-white/80">Users are expected to:</p>
                    <ul className="list-disc pl-6 text-white/80 space-y-2 mt-2">
                      <li>Use the app solely for personal and lawful purposes.</li>
                      <li>Avoid engaging in prohibited activities, including but not limited to:
                        <ul className="list-disc pl-6 space-y-1 mt-2">
                          <li>Cheating, exploiting bugs, or manipulating scores/leaderboards.</li>
                          <li>Attempting to access other users’ accounts or data, or interfering with app security.</li>
                          <li>Sharing content that is hateful, violent, sexual, or otherwise inappropriate for a 5+ audience.</li>
                        </ul>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">1.5 Intellectual Property</h3>
                    <p className="text-white/80">
                      All content within MindRush, including text, images, logos, code, and other materials, is the intellectual property 
                      of DV Tech Ventures Co. You are prohibited from copying, distributing, or modifying any content without prior written consent.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">1.6 Termination</h3>
                    <p className="text-white/80">
                      The Company may suspend or terminate your account if you violate these Terms or engage in any activities deemed harmful 
                      to the platform or its users. Termination does not waive any rights or remedies available to the Company.
                    </p>
                  </div>
                </div>
              </section>

              {/* 2. User Policy */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-6">2. User Policy</h2>
                
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">2.1 Community Standards</h3>
                    <p className="text-white/80">MindRush aims to be a fun, educational, and respectful game for all ages (5+). As a user, you agree to:</p>
                    <ul className="list-disc pl-6 text-white/80 space-y-2 mt-2">
                      <li>Be respectful to other players in friend matches, leagues, and any social features.</li>
                      <li>Not use offensive language, bullying, harassment, or discrimination.</li>
                      <li>Play fairly and avoid cheating, automation, or exploiting bugs.</li>
                    </ul>
                    <p className="text-white/80 mt-2">Violations may result in score resets, temporary restrictions, or account suspension/removal.</p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">2.2 User-Generated Content</h3>
                    <p className="text-white/80">Users may submit limited content such as a username, avatar, feedback, or (where enabled) friend invites. By doing so, you:</p>
                    <ul className="list-disc pl-6 text-white/80 space-y-2 mt-2">
                      <li>Grant the Company a non-exclusive, royalty-free license to use, display, and adapt your content for platform purposes.</li>
                      <li>Agree not to upload content that is illegal, harmful, or violates third-party rights.</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">2.3 Prohibited Conduct</h3>
                    <p className="text-white/80">You agree not to:</p>
                    <ul className="list-disc pl-6 text-white/80 space-y-2 mt-2">
                      <li>Impersonate others or misrepresent your identity.</li>
                      <li>Use automated tools (e.g., bots) to access or interact with the application.</li>
                      <li>Exploit the app for commercial purposes without prior approval.</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">2.4 Reporting Misconduct</h3>
                    <p className="text-white/80">
                      Users can report inappropriate behavior or content by contacting hello@dvtechventures.com. 
                      The Company will investigate and take appropriate action.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">2.5 Accountability for Account Security</h3>
                    <p className="text-white/80">
                      You are responsible for safeguarding your account credentials. Notify the Company immediately if you suspect unauthorized access.
                    </p>
                  </div>
                </div>
              </section>

              {/* 3. Privacy Policy */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-6">3. Privacy Policy</h2>
                
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">3.1 Introduction</h3>
                    <p className="text-white/80">
                      This Privacy Policy explains how MindRush collects, uses, and safeguards your personal data. 
                      By using MindRush, you consent to the practices described herein. If you do not agree, discontinue use of the application.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">3.2 Data Collection</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-lg font-medium text-white mb-2">3.2.1 Personal Information</h4>
                        <p className="text-white/80">We collect personal data you provide directly, such as:</p>
                        <ul className="list-disc pl-6 text-white/80 space-y-2 mt-2">
                          <li><strong>Account details</strong>: email (if you choose email sign-in), or an authentication identifier from Google/Apple/Facebook (if you choose social sign-in).</li>
                          <li><strong>Profile details</strong>: username and optional avatar.</li>
                          <li><strong>Age and education preferences</strong>: age, grade/year system (US/UK), selected subjects, and difficulty preferences so we can personalize questions appropriately.</li>
                          <li><strong>Support communications</strong>: if you contact us, we may store your message and contact details to help you.</li>
                        </ul>
                      </div>

                      <div>
                        <h4 className="text-lg font-medium text-white mb-2">3.2.2 Non-Personal Information</h4>
                        <p className="text-white/80">Automatically collected data includes:</p>
                        <ul className="list-disc pl-6 text-white/80 space-y-2 mt-2">
                          <li><strong>Device and app data</strong>: device model, OS version, app version, language, and general region (derived from IP) for security and analytics.</li>
                          <li><strong>Gameplay and usage data</strong>: quiz sessions, answers, scores, streaks, time-to-answer, coins earned/spent, and feature usage to run the game and improve difficulty matching.</li>
                          <li><strong>Diagnostics</strong>: crash logs and performance metrics to keep the app stable.</li>
                        </ul>
                      </div>

                      <div>
                        <div>
                          <h4 className="text-lg font-medium text-white mb-2">3.2.3 Contacts (Friend Finder)</h4>
                          <p className="text-white/80">
                            If you choose to use <strong>Play with Friends</strong> or <strong>Find Friends</strong>, MindRush may request access to your device contacts to help you discover friends who already use the app.
                            Contacts access is <strong>optional</strong> and is only requested when you tap the friends feature (it is not required to play MindRush).
                          </p>
                          <ul className="list-disc pl-6 text-white/80 space-y-2 mt-2">
                            <li><strong>What we access</strong>: contact identifiers such as phone numbers and/or email addresses (where available in your address book) to perform friend matching.</li>
                            <li><strong>What we do NOT do</strong>: we do not modify or write to your contacts, we do not store your full address book in readable form, and we do not use contacts for advertising.</li>
                            <li><strong>How matching works</strong>: we may transform contact identifiers using a privacy-preserving method (such as one-way hashing) and compare them against identifiers of users who have chosen to be discoverable.</li>
                            <li><strong>Sharing</strong>: we do not sell contacts and we do not share contacts with advertisers. We only use this data to enable the friend and social gameplay features.</li>
                            <li><strong>Your control</strong>: you can deny contacts permission and continue using the game. You can also revoke permission at any time in your device settings.</li>
                          </ul>
                        </div>
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-white mb-2">3.2.4 Sensitive Data</h4>
                        <p className="text-white/80">MindRush is designed as an educational quiz game and does not require sensitive personal data (such as health or medical information). Please do not submit sensitive information through the app.</p>
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-white mb-2">3.2.5 Cookies and Tracking Technologies</h4>
                        <p className="text-white/80">We may use cookies or similar technologies (or mobile identifiers) to:</p>
                        <ul className="list-disc pl-6 text-white/80 space-y-2 mt-2">
                          <li>Remember preferences (such as game mode and education settings).</li>
                          <li>Measure performance and improve the game experience.</li>
                          <li>Support advertising (e.g., rewarded ads) and prevent fraud/abuse.</li>
                        </ul>
                        <p className="text-white/80 mt-2">You can manage tracking permissions via your device settings (and, where applicable, in-app settings).</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">3.3 Data Usage</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-lg font-medium text-white mb-2">3.3.1 Primary Uses</h4>
                        <p className="text-white/80">The data we collect is used to:</p>
                        <ul className="list-disc pl-6 text-white/80 space-y-2 mt-2">
                          <li>Run MindRush features (quizzes, practice, daily challenges, leagues, and friend matches).</li>
                          <li>Personalize question difficulty and educational content based on age/grade/subjects.</li>
                          <li>Maintain fairness (anti-cheat), leaderboards, and match results.</li>
                          <li>Provide customer support, communicate important updates, and keep the app secure.</li>
                        </ul>
                      </div>

                      <div>
                        <h4 className="text-lg font-medium text-white mb-2">3.3.2 Communication</h4>
                        <p className="text-white/80">We use your contact information to send:</p>
                        <ul className="list-disc pl-6 text-white/80 space-y-2 mt-2">
                          <li>Account and security updates.</li>
                          <li>Service messages (e.g., subscription status, important gameplay changes).</li>
                          <li>Optional notifications you enable (e.g., daily reminders).</li>
                        </ul>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-white mb-2">3.3.3 Advertising and Purchases</h4>
                      <p className="text-white/80">
                        MindRush may show ads (including rewarded ads) and may offer in-app purchases and subscriptions (for example, SAT/GMAT preparation packs). Purchases are processed by the Apple App Store or Google Play. We do not receive your full payment card details.
                      </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">3.4 Data Sharing</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-lg font-medium text-white mb-2">3.4.1 With Service Providers</h4>
                        <p className="text-white/80">
                          We share data with trusted service providers strictly to operate the app (for example: hosting, databases, analytics, crash reporting, push notifications, customer support tools, and advertising networks for rewarded ads). These providers are required to protect your information and use it only for providing services to MindRush. We do not share your contacts with advertising networks, and contacts (when used for friend matching) are handled only to enable social gameplay.
                        </p>
                      </div>

                      <div>
                        <h4 className="text-lg font-medium text-white mb-2">3.4.2 For Legal Compliance</h4>
                        <p className="text-white/80">Data may be disclosed:</p>
                        <ul className="list-disc pl-6 text-white/80 space-y-2 mt-2">
                          <li>To comply with legal obligations.</li>
                          <li>To protect users or the Company in cases of fraud or security threats.</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">3.5 Data Security</h3>
                    <p className="text-white/80">MindRush uses reasonable administrative, technical, and physical safeguards (such as encryption in transit, access controls, and monitoring) to protect your data. However, no system is 100% secure. Please use a strong password (if using email sign-in) and keep your device secure.</p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">3.6 User Rights</h3>
                    <p className="text-white/80">You (and, where applicable, a parent/guardian) may have the right to:</p>
                    <ul className="list-disc pl-6 text-white/80 space-y-2 mt-2">
                      <li>Access: request a copy of personal data we hold about the account.</li>
                      <li>Update: change profile details, age/grade/subjects, and preferences in the app.</li>
                      <li>Delete: request deletion of the account and associated personal data.</li>
                      <li>Object/Restrict: limit certain processing where provided by law.</li>
                      <li>Portability: obtain certain data in a machine-readable format where required by law.</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">3.7 Children's Privacy</h3>
                    <p className="text-white/80">
                      MindRush is intended for users ages <strong>5 and up</strong>. If a child is under 13 (or the applicable age of digital consent), a parent/guardian may need to provide consent and can review, delete, or request that we stop collecting personal information from the child. If you believe a child has provided personal information without appropriate consent, contact us and we will take steps to delete it.
                    </p>
                    <p className="text-white/80">
                      Contacts-based friend features are always optional and may be restricted or disabled for child accounts depending on local law or parental consent.
                    </p>
                    <p className="text-white/80 mt-2">Parent/guardian requests can be submitted to hello@dvtechventures.com. To protect the child, we may ask for verification that you are the parent/guardian of the account.</p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">3.8 International Data Transfers</h3>
                    <p className="text-white/80">MindRush may be operated from and store data in countries different from where you live. Where required, we use appropriate safeguards for international transfers. By using MindRush, you understand that your information may be processed in these locations.</p>
                  </div>
                </div>
              </section>

              {/* 4. General Provisions */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-6">4. General Provisions</h2>
                
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">4.1 Governing Law</h3>
                    <p className="text-white/80">These Terms, User Policy, and Privacy Policy are governed by the laws applicable where DV Tech Ventures Co. operates, and disputes will be handled in the appropriate courts with jurisdiction over the matter, unless local consumer laws provide otherwise.</p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">4.2 Contact Information</h3>
                    <p className="text-white/80">For inquiries or concerns, please reach out:</p>
                    <p className="text-white/80 mt-2">Email: hello@dvtechventures.com</p>
                    <p className="text-white/80">Mailing Address: DV Tech Ventures Co., Nairobi, Kenya.</p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">4.3 Changes to This Document</h3>
                    <p className="text-white/80">
                      The Company reserves the right to update this document. Users will be notified of significant changes 
                      via in-app alerts or email. Continued use constitutes acceptance.
                    </p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndPolicy;

